import { useState } from "react";
import { Scale, ShieldCheck, Clock, Check, FileBadge, Loader2, Lock, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { clp } from "@/lib/api";

const PRECIO_INFORME = 4990;
const PRECIO_CAV = 6990;
const PRECIO_CAV_EXPRESS = 2990;

const INCLUYE_360 = [
  "Limitaciones al dominio · prendas · embargos",
  "Encargo por robo, multas y deuda TAG",
  "Historial de propietarios + kilometraje",
  "Tasación de mercado + Tasación fiscal SII",
  "Semáforo de riesgo + QR de verificación",
];

const INCLUYE_CAV = [
  "Todo lo del Informe 360",
  "CAV oficial del Registro Civil (SRCeI)",
  "Prendas, embargos y prohibiciones oficiales",
  "Veredicto legal definitivo",
];

/**
 * Sección única de informes en la landing: compara el Informe 360 ($4.990)
 * con el Informe 360 + CAV oficial ($6.990) lado a lado.
 */
export const InformesPricingSection = ({ login, user }) => {
  const [patente, setPatente] = useState("");
  const [anio, setAnio] = useState("");
  const [loading, setLoading] = useState(false);

  const comprarCav = async () => {
    const p = patente.trim().toUpperCase();
    if (p.length < 5) {
      toast.error("Ingresa una patente válida (ej. ABCD12).");
      return;
    }
    if (!user) {
      toast.message("Inicia sesión para comprar tu Informe + CAV.");
      login();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        package_id: "informe_cav", origin_url: window.location.origin,
        patente: p, anio: anio ? Number(anio) : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos iniciar el pago. Inténtalo nuevamente.");
      setLoading(false);
    }
  };

  return (
    <section id="informe" className="py-16 md:py-24" data-testid="cav-product-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-[#13315C] to-[#13224d] text-white relative overflow-hidden">
          <div className="absolute inset-0 tf-grain opacity-20" />
          <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#F5B301]/15 blur-3xl" />
          <div className="relative p-8 md:p-14">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold mb-5">
                <Sparkles size={14} className="text-[#1F9BD7]" /> Informes con datos oficiales
              </div>
              <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">
                Conoce el auto antes de <span className="text-[#F5B301]">pagar por él</span>
              </h2>
              <p className="mt-4 text-white/70 leading-relaxed">
                Un solo informe reúne las verificaciones oficiales del vehículo: robo, prendas,
                multas, propietarios y tasación. Y si necesitas el dictamen legal definitivo,
                súmale el <strong className="text-white">CAV oficial del Registro Civil</strong>.
              </p>
            </div>

            <div id="informe-cav" className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl">
              {/* Card 1 · Informe 360 */}
              <div className="bg-white rounded-2xl p-6 md:p-7 text-[#13315C] shadow-2xl flex flex-col" data-testid="pricing-card-informe">
                <div className="flex items-center justify-between">
                  <p className="tf-label">Informe 360</p>
                  <ShieldCheck size={20} className="text-[#1F9BD7]" />
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-mono-tf font-black text-4xl" data-testid="informe360-precio">{clp(PRECIO_INFORME)}</span>
                  <span className="text-xs text-slate-400 mb-1.5">por patente · entrega inmediata</span>
                </div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {INCLUYE_360.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={15} className="text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-6 w-full h-12 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-xl font-semibold" data-testid="informe-cta-btn">
                  <a href="/#consulta-patente">Consultar mi patente</a>
                </Button>
                <p className="mt-3 text-[11px] text-slate-400 text-center">Tu primera consulta de patente es gratis</p>
              </div>

              {/* Card 2 · Informe 360 + CAV */}
              <div className="bg-white rounded-2xl p-6 md:p-7 text-[#13315C] shadow-2xl ring-2 ring-[#F5B301] relative flex flex-col" data-testid="pricing-card-cav">
                <span className="absolute -top-3 left-6 rounded-full bg-[#F5B301] text-[#13315C] text-[10px] font-black uppercase tracking-wider px-3 py-1">
                  Más completo
                </span>
                <div className="flex items-center justify-between">
                  <p className="tf-label">Informe 360 + CAV oficial</p>
                  <Scale size={20} className="text-[#10B981]" />
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-mono-tf font-black text-4xl">{clp(PRECIO_CAV)}</span>
                  <span className="text-xs text-slate-400 mb-1.5">pago único</span>
                </div>
                <ul className="mt-5 space-y-2.5">
                  {INCLUYE_CAV.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={15} className="text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 space-y-2.5 flex-1">
                  <Input
                    value={patente}
                    onChange={(e) => setPatente(e.target.value.toUpperCase())}
                    placeholder="Patente (ej. ABCD12)" maxLength={6}
                    className="h-11 uppercase font-mono-tf"
                    data-testid="cav-product-patente"
                    onKeyDown={(e) => e.key === "Enter" && comprarCav()}
                  />
                  <Input
                    value={anio}
                    onChange={(e) => setAnio(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="Año (opcional)" inputMode="numeric" maxLength={4}
                    className="h-11 font-mono-tf"
                    data-testid="cav-product-anio"
                    onKeyDown={(e) => e.key === "Enter" && comprarCav()}
                  />
                </div>
                <Button
                  onClick={comprarCav} disabled={loading}
                  className="mt-3 w-full h-12 bg-[#13315C] hover:bg-[#1C4076] text-white rounded-xl font-semibold"
                  data-testid="cav-product-buy-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <><FileBadge size={17} className="mr-1.5" /> Comprar Informe + CAV</>}
                </Button>
                <p className="mt-3 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                  <Lock size={11} /> Pago seguro · <Clock size={11} /> CAV en 24 horas hábiles
                </p>
              </div>
            </div>

            <p className="mt-6 text-sm text-white/50 max-w-4xl" data-testid="cav-express-crosssell">
              ¿Ya tienes tu Informe 360? Agrega solo el CAV oficial por <strong className="text-white/80">{clp(PRECIO_CAV_EXPRESS)}</strong> (CAV
              Express) directamente desde tu informe.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
